import Vue from 'vue';
import BaseComponent from './BaseComponentMixin.jsx';
import utils from '../../Shared/utils.jsx';
import webSocket from '@/Services/webSocketService';
import EventBus from '@/Application/event-bus';
import careHelpfulFunctions from '@/Application/careHelpfulFunctions.jsx';
import apiService from '@/Services/api';
import defaultTheme from '@/plugins/themes/DefaultTheme';
import { CreateWebSocketConnection } from '@/Services/webSocketConnectionFactory';

Vue.component('rt-event-handler', {
    mixins: [BaseComponent],
    data: function () {
        return {
            isActive: false,
            readyToActivate: false,
            errorState: '',
            // Why the stream is (in)active - published as Status so cards can
            // distinguish a transient reconnect from a revoked/deleted key:
            // '' | 'Active' | 'Closed' | 'Reconnecting' | 'Refused' | 'ConnectFailed' | 'Error'
            connectionStatus: '',
            cache: {                
                widget_views: {}
            },
            requestedViews: [],

            // EmbeddedCrossCenter (AutoReconnect) state
            reconnectTimer: null,
            reconnecting: false,
            reconnectStartedAt: 0,
            connectAttempt: 0,
            subscribeRefusals: 0,
            lastSubscribeAttempt: 0,
            connectFailures: 0,
            lastConnectAttempt: 0,
            isDestroyed: false
        }
    },
    props: {},
    computed: {
        apiKey: function () {
            if (!this.apiKey_fn && this.controlData.ApiKey)
                this.apiKey_fn = utils.compile(this, this.controlData.ApiKey);

            return this.apiKey_fn ? utils.evaluate(this.apiKey_fn, this) : '';
        },
        apiBaseUri: function () {
            if (!this.apiBaseUri_fn && this.controlData.ApiBaseUrl)
                this.apiBaseUri_fn = utils.compile(this, this.controlData.ApiBaseUrl);

            return this.apiBaseUri_fn ? utils.evaluate(this.apiBaseUri_fn, this) : '';
        },
    },
    created() {
        if (this.controlData.PublishField) {

            // User/Interactions lists are no longer provided (based on RTEvent deltas)
            const self = this;
            const accessor = {                
                get Views() {
                    return self.cache.widget_views;
                },               
                get IsActive() {
                    return self.isActive;
                },
                get Status() {
                    return self.connectionStatus;
                },
                APIRequest: this.APIRequest, // returns a function
                apipatch: this.apipatch,
                apiput: this.apiput,
                apipost: this.apipost,
                apidelete_: this.apidelete_,
                apiget: this.apiget,
                AddView: this.AddView,
                RemoveView: this.RemoveView,
                GetNamedListForRealTime: this.GetNamedListForRealTime,
                RemoveNamedListForRealTime: this.RemoveNamedListForRealTime,
                TryToGetResponse: this.TryToGetResponse,
            };

            // Using a function here to allow PublishField to contain dot notation or other valid Javascript syntax
            // The resulting code would then effectively do this (using GlobalVars.RTData as PublishField):
            // this.GlobalVars.RTData = accessor;

            // EmbeddedCrossCenter: allow the publish target to be templated, e.g. a
            // per-source instance inside a ForEach:
            //   GlobalVars.EmbeddedCrossCenter['{{ SourceData().Name }}'].RTData
            // Plain values (no braces) keep the historical raw-string path.
            let publishField = this.controlData.PublishField;
            if (publishField.includes('{{'))
                publishField = utils.evaluate(utils.compile(this, publishField), this);

            let code = `base.${publishField} = value;`;
            let func = new Function('base', 'value', code);
            func(this, accessor);
        }

        // IsolateEvents prevents this instance from ingesting the local (native)
        // center's real-time events when it is dedicated to a remote center's
        // stream and rendered alongside native dashboards on the same page.
        if (!this.controlData.IsolateEvents)
            EventBus.$on('RealTimeBatch', this.rtEventHandler);
    },
    //Mounted Replaced with preRenderComplete
    destroyed() {
        this.isDestroyed = true;

        EventBus.$off('RealTimeBatch', this.rtEventHandler);
        EventBus.$off('WebSocketConnected', this.activate);

        if (this.reconnectTimer) {
            clearInterval(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.controlData.Native)
            webSocket.StopRealTimeMonitor();
        else if (this.standAloneWebSocket) {
            // Cross-center: close our own standalone socket; do NOT touch the
            // shared native websocket service.
            try { this.standAloneWebSocket.Disconnect(); } catch (e) { /* already closed */ }
            this.standAloneWebSocket = null;
        }
    },
    watch: {
        todisplay: function (value, old) {
            if (value && !old && this.readyToActivate)
                this.activate();
        }
    },
    methods: {
        async preRenderComplete() {
            this.finishRenderHandler(this);

            this.readyToActivate = true;

            if (!this.todisplay)
                return;

            //await this.activate();
        },
        async activate() {
            try {
                if (this.controlData.Native) {
                    await webSocket.StartRealTimeMonitorForView();
                    this.RestoreViews();
                    this.isActive = true;
                    EventBus.$once('WebSocketConnected', this.activate);
                }
                else {
                    await this.connect();

                    // Mirror the native websocketService's ensureConnection pattern:
                    // recover from network drops, server restarts, refused
                    // subscriptions (pending approval), and deliberate server-side
                    // disconnects (stream approval changes / revocation).
                    if (this.controlData.AutoReconnect)
                        this.startEnsureConnectionTimer();
                }
            } catch (err) {
                utils.error('Error activating RTEventHandler', err);
            }
        },
        async connect() {
            // Establish a custom stand-alone websocket to listen for real-time events
            // from an arbitrary web server / call center.

            try {
                // Guard against connect() being invoked while a previous socket exists
                // (e.g. todisplay toggling activate() again) - never orphan a live socket.
                if (this.standAloneWebSocket) {
                    try { this.standAloneWebSocket.Disconnect(); } catch (e) { /* already closed */ }
                    this.standAloneWebSocket = null;
                }

                this.standAloneWebSocket = await CreateWebSocketConnection(this.apiBaseUri);
                await this.standAloneWebSocket.Connect(
                    //baseuri
                    this.apiBaseUri,
                    //queryParams
                    { 'apikey': this.apiKey },
                    //noticeCallback
                    (message, arg, connectionId, time) => { utils.log(`Notice: ${message}, ${arg}, ${connectionId}, ${time}`); },
                    //channelEventCallback
                    (event) => { utils.log(`Event: ${JSON.stringify(event)}`); },
                    //rtEventCallback
                    this.rtEventHandler,
                    //onCloseCallback - the server-side force-disconnect (revoked key or
                    //scope change) sends 'Close' and expects the CLIENT to drop the
                    //transport; the server only cleans up its own session state. If we
                    //stay connected, ensureConnection sees a healthy socket and the
                    //boards freeze on stale data forever. Drop the socket so the 5s
                    //ensure-loop reconnects: a revoked key is then refused (views blank,
                    //Disconnected badge), a re-scoped key resubscribes under the new
                    //filter and RestoreViews repopulates.
                    (error) => {
                        utils.log(`RTEventHandler server requested close: ${this.apiBaseUri}`);
                        this.isActive = false;
                        this.connectionStatus = 'Closed';
                        if (this.standAloneWebSocket) {
                            try { this.standAloneWebSocket.Disconnect(); } catch (e) { /* already closed */ }
                            this.standAloneWebSocket = null;
                        }
                    });
                await this.subscribe();
            }
            catch (err) {
                utils.warn(`RTEventHandler standalone WebSocket failedr: ${this.apiBaseUri}`, err);

                // The socket could not be established at all (typically a 401 - the
                // source center revoked or deleted the key - or the center is
                // unreachable). Clear the boards; leaving the last snapshot on screen
                // under a "Disconnected" badge reads as live data.
                this.isActive = false;
                this.connectionStatus = 'ConnectFailed';
                this.errorState = `Connect failed: ${err}`;
                this.blankCachedViews();
            }
        },
        blankCachedViews() {
            // Drop every cached view so the grids clear WITHOUT a reload - grids watch
            // these entries and would otherwise keep showing the last data indefinitely.
            // Used when the subscription is refused (403) and when the socket cannot be
            // established at all (revoked/deleted key). RestoreViews repopulates them
            // if the subscription is later re-approved.
            for (const key in this.cache.widget_views) {
                const v = this.cache.widget_views[key];
                if (v && (v.Total || (v.Data && v.Data.length)))
                    Vue.set(this.cache.widget_views, key, { Total: 0, Data: [] });
            }
        },
        async subscribe() {
            // Cause the web server to send us RT events (real-time stats)
            try {
                const status = await this.standAloneWebSocket.StartRealTimeMonitorForView();

                // EmbeddedCrossCenter servers return Forbidden (403) when the stream is
                // pending approval or revoked; Created (201) when streaming. Only honor
                // the refusal when AutoReconnect is set so legacy deployments keep
                // their historical behavior (isActive true regardless of status).
                if (this.controlData.AutoReconnect && !(status === undefined || status === null || status == 201 || status == 'Created')) {
                    this.isActive = false;
                    this.connectionStatus = 'Refused';
                    this.errorState = `Subscription refused (pending approval or revoked): ${status}`;
                    utils.log(`RTEventHandler subscription refused: ${this.apiBaseUri} status:${status}`);

                    // Blank any cached views so dashboards reflect the revocation without a reload.
                    this.blankCachedViews();
                }
                else {
                    this.isActive = true;
                    this.connectionStatus = 'Active';
                }
            }
            catch (e) {
                this.errorState = JSON.stringify(e);
                this.isActive = false;
                this.connectionStatus = 'Error';
                utils.warn(`RTEventHandler failed to invoke StartRealTimeMonitorForView: ${this.apiBaseUri}`, e);
            }
        },

        startEnsureConnectionTimer() {
            // Cross-center only: watch the standalone websocket AND the subscription
            // state. Covers: network drops, source web server restarts, server-side
            // force-disconnects (approval changed / revoked - the server sends 'Close'
            // and the transport drops), and refused subscriptions (pending approval -
            // the socket stays connected, so we retry the subscription itself).
            if (this.reconnectTimer)
                return;

            this.reconnectTimer = setInterval(() => this.ensureConnection(), 5000);
        },
        async ensureConnection() {
            if (this.reconnecting) {
                // A previous attempt may be wedged on a hung network call (the
                // connection factory's fetch has no timeout). After 30s, abandon it
                // so the loop cannot stall forever; the attempt-token check below
                // discards the stale socket if that attempt ever completes.
                if (Date.now() - this.reconnectStartedAt > 30000) {
                    utils.warn(`RTEventHandler reconnect attempt abandoned (timeout): ${this.apiBaseUri}`);
                    this.connectAttempt++; // invalidate the wedged attempt
                    this.reconnecting = false;
                }
                return;
            }

            const connected = this.standAloneWebSocket && this.standAloneWebSocket.IsConnected();

            if (connected && this.isActive)
                return;

            if (connected && !this.isActive) {
                // Connected but the subscription was refused (pending approval or
                // revoked). Retry it without tearing the socket down - but back off
                // to once per minute after ~1 minute of refusals so a permanently
                // revoked stream doesn't hammer the source center.
                if (this.subscribeRefusals >= 12 && (Date.now() - this.lastSubscribeAttempt) < 60000)
                    return;

                this.lastSubscribeAttempt = Date.now();
                await this.subscribe();

                if (this.isActive) {
                    this.subscribeRefusals = 0;
                    this.RestoreViews();
                }
                else
                    this.subscribeRefusals++;

                return;
            }

            // Not connected: rebuild the socket from scratch - but back off to once
            // per minute after ~1 minute of consecutive failures. Without this a
            // deleted key (401 on every negotiate) rebuilds the socket every 5s
            // against the source center forever.
            if (this.connectFailures >= 12 && (Date.now() - this.lastConnectAttempt) < 60000)
                return;

            this.lastConnectAttempt = Date.now();
            this.reconnecting = true;
            this.reconnectStartedAt = Date.now();
            const attempt = ++this.connectAttempt;
            try {
                utils.log(`RTEventHandler standalone websocket disconnected, attempting reconnect: ${this.apiBaseUri}`);
                this.isActive = false;
                if (this.connectionStatus != 'ConnectFailed')
                    this.connectionStatus = 'Reconnecting';

                await this.connect(); // connect() disconnects any old socket, then calls subscribe()

                if (attempt != this.connectAttempt || this.isDestroyed) {
                    // Superseded by a newer attempt, or the component was destroyed
                    // while we were connecting - release the socket we just created.
                    if (this.standAloneWebSocket) {
                        try { this.standAloneWebSocket.Disconnect(); } catch (e) { /* already closed */ }
                        this.standAloneWebSocket = null;
                    }
                    return;
                }

                if (this.isActive) {
                    this.subscribeRefusals = 0;
                    this.connectFailures = 0;
                    this.RestoreViews();
                }
                else if (this.connectionStatus == 'ConnectFailed')
                    this.connectFailures++;
            }
            catch (e) {
                utils.warn('RTEventHandler reconnect attempt failed', e);
            }
            finally {
                this.reconnecting = false;
            }
        },     
        
         rtEventHandler(events) {
            if (!Array.isArray(events)) {
                console.log('Unhandled signalR Event (2): ' + JSON.stringify(events));
                return;
            }

            const subitems = events
                .filter(item => typeof item === 'string')
                .flatMap(item => {
                    try {
                       
                        const items = JSON.parse(item);
                        if (Array.isArray(items))
                            return items;

                        if (typeof items === 'object' && items !== null) 
                            return [items];

                        return [];       

                    } catch(e) {
                        utils.error('Error parsing JSON RTEvent:', item, e)
                    }
                });

            for(const subitem of subitems) {
                utils.debug(`signalR RTEvent:  ${JSON.stringify(subitem)}`);
                if (subitem?.Type === 'WidgetView') {                    
                    switch (subitem.Operation) {
                    case 'Add': 
                        this.updateWidgetView(subitem);                        
                        break;                                                

                    case 'Remove':
                        this.removeWidgetView(subitem);
                        break;

                    default:
                        utils.log('Missing or unsupported operation: ' + subitem?.Operation);
                        break;
                    }
                } else {                    
                    console.log('Unhandled RTEvent: ' + JSON.stringify(subitem));
                }
            }            
        }, 
       
        
        updateWidgetView(item) {
            // Give each row a unique identifier for the realtime grid to correctly know when to add or remove rows
            if (item.Data && item.Data.Data && Array.isArray(item.Data.Data) && item.Data.Data.length) {
                for (let i = 0; i < item.Data.Data.length; i++)
                    Vue.set(item.Data.Data[i], '_id', i);
            } 
            //utils.log(`RTView updateWidgetView(); item.Name:${item.Name}; item.Data:${JSON.stringify(item.Data)}`);

            Vue.set(this.cache.widget_views, item.Name, item.Data);
        },

        removeWidgetView(item) {

            Vue.delete(this.cache.widget_views, item.Name);
        },

        async APIRequest(options) {
            const apiOptions = { ...options };

            if (!this.controlData.Native)
            {
                apiOptions.url = this.apiBaseUri;

                if (options.url.startsWith('/'))
                    apiOptions.url += options.url;
                else
                    apiOptions.url += '/' + options.url;

                if (!apiOptions.headers)
                    apiOptions.headers = {};

                apiOptions.headers.apikey = this.apiKey;
                apiOptions.doNotUseWebsocket = true; // ON CROSS CENTER WE CANNOT USE WEBSOCKETS

                // Cross-center calls authenticate with the SOURCE center's api key, not
                // our access token. A 401 here means that key was revoked or deleted -
                // it must never be mistaken for our own session expiring (which would
                // log the local user out), and it must never be replayed from the
                // post-login request queue.
                apiOptions.doNotTriggerLogin = true;
                apiOptions.doNotQueue = true;
            }

            return await apiService.apiRequest(apiOptions);
        },
        async apiget(url) {
            return await  this.APIRequest({
                method: 'GET',
                url: url,
                flatten: true
            });
        },
        async apidelete_(url) {
            return await this.APIRequest({
                method: 'DELETE',
                url: url,
                flatten: true
            });
        },
        async apipost(url, data) {
            return await this.APIRequest({
                method: 'POST',
                data: data,
                url: url,
                flatten: true
            });
        },
        async apiput(url, data) {
            return await this.APIRequest({
                method: 'PUT',
                data: data,
                url: url,
                flatten: true
            });
        },
        async apipatch(url, data) {
            return await  this.APIRequest({
                method: 'PATCH',
                data: data,
                url: url,
                flatten: true
            });
        },

        RestoreViews() {
            utils.debug(`RestoreViews() ${this.requestedViews.length} item(s)`);

            // Clone the array to allow the functions to modify it
            for (let view of [...this.requestedViews])
            {
                utils.debug(`RestoreViews() view:${JSON.stringify(view)}`);

                switch (view.type) {
                    case 'view':
                        this.AddView(view.name, view.args, true);
                        break;

                    case 'list':
                        this.GetNamedListForRealTime(view.listName, view.sortCol, view.descending, view.start, view.length, view.columns, view.filters, view.id);
                        break;
                }
            }
        },
        async AddView(viewName, args, restore) {
            if (restore) {
                // No need to do any tracking if we are just restoring
                if (this.controlData.Native)
                    webSocket.AddRealTimeMonitorView(viewName, args);
                else if (this.standAloneWebSocket)
                    this.standAloneWebSocket.AddRealTimeMonitorView(viewName, args);
                return;
            }
            //console.log(`RTEventHandler.AddView(viewName:${viewName},args:${args ? JSON.stringify(args) : ''})`);

            const idx = this.requestedViews.findIndex(a => a.type == 'view' && a.name == viewName && JSON.stringify(a.args) == JSON.stringify(args));

            if (idx >= 0)
                this.requestedViews[idx].refCount++;

            let res;
            if (this.controlData.Native)
                res = await webSocket.AddRealTimeMonitorView(viewName, args);
            else if (this.standAloneWebSocket)
                res = await this.standAloneWebSocket.AddRealTimeMonitorView(viewName, args);

            let id;
            if (res && res.statusCode == 200)
                id = res.responseBody;

            // Save the view requested so that if we detect a reset websocket, we can re-register (ensure we don't add a duplicate)
            if (idx < 0)
                this.requestedViews.push({
                    type: 'view',
                    name: viewName,
                    args: args,
                    id: id,
                    refCount: 1,
                });

            return id; // ? JSON.parse(id) : null;
        },
        RemoveView(viewName, args) {
            //console.log(`RTEventHandler.RemoveView(viewName:${viewName},args:${args ? JSON.stringify(args) : ''})`);
            const idx = this.requestedViews.findIndex(a => a.type == 'view' && a.name == viewName && JSON.stringify(a.args) == JSON.stringify(args));

            if (idx >= 0)
                this.requestedViews[idx].refCount--;

            if (idx < 0 || this.requestedViews[idx].refCount <= 0) {
                // If there are no more references to this view, we should unsubscribe to stop the server from sending it
                if (this.controlData.Native)
                    webSocket.RemoveRealTimeMonitorView(viewName, args);
                else if (this.standAloneWebSocket)
                    this.standAloneWebSocket.RemoveRealTimeMonitorView(viewName, args);
            }

            if (idx >= 0 && this.requestedViews[idx].refCount <= 0) {
                this.requestedViews.splice(idx, 1);
                
                let cacheViewName = viewName;
                if (args && JSON.stringify(args) != '{}')
                cacheViewName += '~!!~' + JSON.stringify(args);
                Vue.delete(this.cache.widget_views, cacheViewName);
            }

        },
        async GetNamedListForRealTime(listName, sortCol, descending, start, length, columns, filters, id) {
            utils.debug(`GetNamedListForRealTime(listName:${listName}, sortCol:${sortCol}, descending:${descending}, start:${start}, length:${length}, columns:${JSON.stringify(columns)}, filters:${JSON.stringify(filters)}, id:${id})`);

            if (id === undefined || id === null)
                id = utils.generateUUID();

            // Before saving, ensure we don't add a duplicate
            var existingView = this.requestedViews.find(a => a.type == 'list' && a.id == id);

            const args = {
                listName: listName,
                id: id,
                sortCol: sortCol,
                descending: descending,
                start: start,
                length: length,
                columns: columns,
                filters: filters,
            };

            // Save the view requested so that if we detect a reset websocket, we can re-register
            if (!existingView)
                this.requestedViews.push({ type: 'list', ...args });
            else
                Object.assign(existingView, args);

            let data;
            if (this.controlData.Native)
                data = await webSocket.GetNamedListForRealTime(listName, id, sortCol, descending, start, length, columns, filters);
            else if (this.standAloneWebSocket)
                data = await this.standAloneWebSocket.GetNamedListForRealTime(listName, id, sortCol, descending, start, length, columns, filters);

            // SignalR backwards compatability
            var statusCode = data.StatusCode ? data.StatusCode : data.statusCode;
            var responseBody = data.ResponseBody ? data.ResponseBody : data.responseBody;

            if (data && statusCode == 200 && responseBody) {
                data = responseBody;

                // The data contains the snapshot of the report data we want, but it will also
                // be registered on the web server to send regular updates for that same query.
                // The RTData will arrive under the ViewHash, which is why we need to know. The
                // ID is part of the ViewHash, but if we need to alter our query parameters
                // because, for example, the user changes sort order, page number, etc., then
                // we need to update the same query without causing the server to cache yet
                // another view. So the ID is important for that.
                return {
                    Response: data,
                    ViewHash: `${listName}~!!~${id}`,
                    ID: id,
                };
            }

            throw `GetNamedListForRealTime failed: ${JSON.stringify(data)}`;
        },
        async RemoveNamedListForRealTime(listName, id) {
            utils.debug(`RemoveNamedListForRealTime(listName:${listName}, id:${id}`);

            // Before saving, ensure we don't add a duplicate
            const idx = this.requestedViews.findIndex(a => a.type == 'list' && a.id == id);

            // Save the view requested so that if we detect a reset websocket, we can re-register
            if (idx >= 0)
                this.requestedViews.splice(idx, 1);

            if (this.controlData.Native)
                await webSocket.RemoveNamedListForRealTime(listName, id);
            else if (this.standAloneWebSocket)
                await this.standAloneWebSocket.RemoveNamedListForRealTime(listName, id);

            var cacheViewName = listName + '~!!~' + id;
            Vue.delete(this.cache.widget_views, cacheViewName);
        },
        async TryToGetResponse() {
            if (this.controlData.Native)
                return await webSocket.TryToGetResponse();
            else if (this.standAloneWebSocket)
                return await this.standAloneWebSocket.TryToGetResponse();
        },
    },
    render(h) {
        if (this.isdebug) {
            let icon = <v-icon small color={this.isActive ? 'purple' : 'red'}>mdi-chart-pie</v-icon>;
            let tooltip;
            if (this.isActive)
                tooltip = this.Translate('Real-Time Event Listener Active (Debug is enabled)');
            else if (this.errorState)
                tooltip = this.errorState;
            else
                tooltip = this.Translate('Real-Time Event Listener Waiting... (Debug is enabled)');

            icon = utils.generateTooltip(h, icon, tooltip, 'right');

            return (
                <div class="c-RTEventHandler" style="border: 1px solid silver; border-radius: 3px;">
                    {icon}
                </div>
            );
        }

        return null;
    }
});